import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { onboardingStore } from "../auth/onboardingStore";
import { useAuth } from "../auth/AuthContext";
import type { ProfileAnswers } from "../domain/answers";
import { emptyProfileAnswers } from "../domain/answers";
import type { MediaAsset } from "../domain/types";
import { PHOTO_MAX } from "../media/limits";
import { revokeMany, revokeMediaAsset } from "../media/createMediaAsset";
import { resolveStoredPreviewUrl } from "../media/uploadService";
import { PHASES, STEP_ORDER, phaseForStep } from "./steps";
import type { FlowAnswers, Phase, ScaleKey, StepId } from "./steps";
import { canProceed } from "./validation";
import { proceedHint } from "./proceedHint";
import { canUseLocalStorage } from "../storage/localWrite";
import { useT } from "../i18n";

type RuleKey = keyof FlowAnswers["rules"];

interface FlowContextValue {
  stepId: StepId;
  stepIndex: number;
  phase: Phase | null;
  phases: typeof PHASES;
  answers: FlowAnswers;
  profileAnswers: ProfileAnswers;
  userId: string | null;
  canContinue: boolean;
  continueHint: string | null;
  storageWarning: string | null;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  back: () => void;
  goToStep: (id: StepId) => void;
  setChristian: (v: boolean | null) => void;
  setRule: (key: RuleKey, v: boolean) => void;
  setIntent: (id: string) => void;
  setAccount: (email: string, password: string) => void;
  setGovernmentId: (asset: MediaAsset | null) => void;
  setConfirmationVideo: (asset: MediaAsset | null) => void;
  addPhotos: (assets: MediaAsset[]) => void;
  removePhoto: (id: string) => void;
  setWhyChristian: (v: string) => void;
  setScale: (key: ScaleKey, value: number) => void;
  setAboutYou: (v: string) => void;
  setLookingFor: (v: string) => void;
  setPreferredPartnerCountry: (v: "same" | "any" | string) => void;
  setLoveReady: (v: boolean | null) => void;
  setLifestyle: (
    hobbies: string,
    reading: number | null,
    profession: string,
    lifestyleNotes?: string
  ) => void;
  setProfile: (
    firstName: string,
    age: string,
    city: string,
    gender: "male" | "female" | null,
    country?: string,
    preferredPartnerCountry?: "same" | "any" | string
  ) => void;
  /** Replace the whole questionnaire tree (screens patch nested slices). */
  setProfileAnswers: (next: ProfileAnswers | ((prev: ProfileAnswers) => ProfileAnswers)) => void;
}

export const initialAnswers: FlowAnswers = {
  isChristian: null,
  rules: {
    respectful: false,
    noSexualContent: false,
    noDominance: false,
  },
  intent: null,
  email: "",
  password: "",
  governmentId: null,
  confirmationVideo: null,
  profilePhotos: [],
  whyChristian: "",
  scales: {
    loyalty: null,
    honesty: null,
    passion: null,
    commitment: null,
    social: null,
    creativity: null,
    humor: null,
  },
  aboutYou: "",
  lookingFor: "",
  loveReady: null,
  hobbies: "",
  lifestyleNotes: "",
  reading: null,
  profession: "",
  firstName: "",
  age: "",
  city: "",
  country: "",
  preferredPartnerCountry: "any",
  gender: null,
};

const FlowContext = createContext<FlowContextValue | null>(null);

export function FlowProvider({ children }: { children: ReactNode }) {
  const { userId, session } = useAuth();
  const t = useT();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<FlowAnswers>(initialAnswers);
  const [profileAnswers, setProfileAnswersState] = useState<ProfileAnswers>(emptyProfileAnswers);
  const [hydrated, setHydrated] = useState(false);
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    const prev = lastUserId.current;
    lastUserId.current = userId;

    if (!userId) {
      if (prev) {
        setStepIndex(0);
        setAnswers(initialAnswers);
        setProfileAnswersState(emptyProfileAnswers());
      } else {
        const draft = onboardingStore.loadDraft();
        if (draft) {
          setStepIndex(draft.stepIndex);
          setAnswers({
            ...initialAnswers,
            ...draft.answers,
            password: "",
          });
          setProfileAnswersState(draft.profileAnswers ?? emptyProfileAnswers());
        }
      }
      setHydrated(true);
      return;
    }

    if (prev === userId) {
      setHydrated(true);
      return;
    }

    const saved = onboardingStore.load(userId);
    if (saved) {
      setStepIndex(saved.stepIndex);
      setAnswers({
        ...initialAnswers,
        ...saved.answers,
        email: session?.email ?? saved.answers.email,
        password: "",
      });
      setProfileAnswersState(saved.profileAnswers ?? emptyProfileAnswers());
    } else if (prev === null) {
      setAnswers((a) => ({
        ...a,
        email: session?.email ?? a.email,
        password: "",
      }));
    } else {
      setStepIndex(0);
      setAnswers({
        ...initialAnswers,
        email: session?.email ?? "",
      });
      setProfileAnswersState(emptyProfileAnswers());
    }
    setHydrated(true);
  }, [userId, session?.email]);

  useEffect(() => {
    if (!hydrated || !userId) return;
    let cancelled = false;
    void (async () => {
      const saved = onboardingStore.load(userId);
      if (!saved) return;

      const hydrateOne = async (asset: MediaAsset | null): Promise<MediaAsset | null> => {
        if (!asset?.storageKey) return asset;
        if (asset.publicUrl) return asset;
        if (asset.objectUrl && !asset.objectUrl.startsWith("blob:")) return asset;
        const url = await resolveStoredPreviewUrl(asset);
        return url ? { ...asset, objectUrl: url } : asset;
      };

      const governmentId = await hydrateOne(saved.answers.governmentId);
      const confirmationVideo = await hydrateOne(saved.answers.confirmationVideo);
      const profilePhotos = await Promise.all(
        saved.answers.profilePhotos.map(async (p) => (await hydrateOne(p))!)
      );
      if (cancelled) return;

      setAnswers((a) => ({
        ...a,
        governmentId: governmentId ?? a.governmentId,
        confirmationVideo: confirmationVideo ?? a.confirmationVideo,
        profilePhotos: profilePhotos.length ? profilePhotos : a.profilePhotos,
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, userId]);

  useEffect(() => {
    if (!hydrated) return;
    if (!canUseLocalStorage()) {
      setStorageWarning(t("common.storageDisabled"));
      return;
    }
    const result = userId
      ? onboardingStore.save(userId, stepIndex, answers, profileAnswers)
      : onboardingStore.saveDraft(stepIndex, answers, profileAnswers);
    if (result.ok) {
      setStorageWarning(null);
      if (userId) onboardingStore.clearDraft();
    } else {
      setStorageWarning(
        result.reason === "quota" ? t("common.storageQuota") : t("common.storageDisabled")
      );
    }
  }, [hydrated, userId, stepIndex, answers, profileAnswers, t]);

  const stepId = STEP_ORDER[stepIndex] ?? "welcome";
  const phase = phaseForStep(stepId);

  const next = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, STEP_ORDER.length - 1));
  }, []);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goToStep = useCallback((id: StepId) => {
    const idx = STEP_ORDER.indexOf(id);
    if (idx >= 0) setStepIndex(idx);
  }, []);

  const setGovernmentId = useCallback((asset: MediaAsset | null) => {
    setAnswers((a) => {
      if (a.governmentId && a.governmentId.id !== asset?.id) {
        revokeMediaAsset(a.governmentId);
      }
      return { ...a, governmentId: asset };
    });
  }, []);

  const setConfirmationVideo = useCallback((asset: MediaAsset | null) => {
    setAnswers((a) => {
      if (a.confirmationVideo && a.confirmationVideo.id !== asset?.id) {
        revokeMediaAsset(a.confirmationVideo);
      }
      return { ...a, confirmationVideo: asset };
    });
  }, []);

  const addPhotos = useCallback((incoming: MediaAsset[]) => {
    setAnswers((a) => {
      const room = PHOTO_MAX - a.profilePhotos.length;
      if (room <= 0) {
        revokeMany(incoming);
        return a;
      }
      const accepted = incoming.slice(0, room);
      revokeMany(incoming.slice(room));
      return { ...a, profilePhotos: [...a.profilePhotos, ...accepted] };
    });
  }, []);

  const removePhoto = useCallback((id: string) => {
    setAnswers((a) => {
      const target = a.profilePhotos.find((p) => p.id === id);
      if (target) revokeMediaAsset(target);
      return { ...a, profilePhotos: a.profilePhotos.filter((p) => p.id !== id) };
    });
  }, []);

  const setProfileAnswers = useCallback(
    (next: ProfileAnswers | ((prev: ProfileAnswers) => ProfileAnswers)) => {
      setProfileAnswersState((prev) => (typeof next === "function" ? next(prev) : next));
    },
    []
  );

  const value = useMemo<FlowContextValue>(
    () => ({
      stepId,
      stepIndex,
      phase,
      phases: PHASES.map((p) => ({
        id: p.id,
        label: t(`common.phases.${p.id}`),
      })),
      answers,
      profileAnswers,
      userId,
      canContinue: canProceed(stepId, answers, profileAnswers),
      continueHint: canProceed(stepId, answers, profileAnswers)
        ? null
        : proceedHint(stepId, answers, profileAnswers, t),
      storageWarning,
      isFirst: stepIndex === 0,
      isLast: stepIndex === STEP_ORDER.length - 1,
      next,
      back,
      goToStep,
      setChristian: (v) => setAnswers((a) => ({ ...a, isChristian: v })),
      setRule: (key, v) =>
        setAnswers((a) => ({
          ...a,
          rules: { ...a.rules, [key]: v },
        })),
      setIntent: (id) => setAnswers((a) => ({ ...a, intent: id })),
      setAccount: (email, password) => setAnswers((a) => ({ ...a, email, password })),
      setGovernmentId,
      setConfirmationVideo,
      addPhotos,
      removePhoto,
      setWhyChristian: (v) => setAnswers((a) => ({ ...a, whyChristian: v })),
      setScale: (key, val) =>
        setAnswers((a) => ({
          ...a,
          scales: {
            loyalty: a.scales.loyalty ?? null,
            honesty: a.scales.honesty ?? null,
            passion: a.scales.passion ?? null,
            commitment: a.scales.commitment ?? null,
            social: a.scales.social ?? null,
            creativity: a.scales.creativity ?? null,
            humor: a.scales.humor ?? null,
            [key]: val,
          },
        })),
      setAboutYou: (v) => setAnswers((a) => ({ ...a, aboutYou: v })),
      setLookingFor: (v) => setAnswers((a) => ({ ...a, lookingFor: v })),
      setPreferredPartnerCountry: (v) =>
        setAnswers((a) => ({ ...a, preferredPartnerCountry: v })),
      setLoveReady: (v) => setAnswers((a) => ({ ...a, loveReady: v })),
      setLifestyle: (hobbies, reading, profession, lifestyleNotes) =>
        setAnswers((a) => ({
          ...a,
          hobbies,
          reading,
          profession,
          lifestyleNotes:
            lifestyleNotes !== undefined ? lifestyleNotes : a.lifestyleNotes,
        })),
      setProfile: (firstName, age, city, gender, country, preferredPartnerCountry) =>
        setAnswers((a) => ({
          ...a,
          firstName,
          age,
          city,
          gender,
          ...(country !== undefined ? { country } : {}),
          ...(preferredPartnerCountry !== undefined
            ? { preferredPartnerCountry }
            : {}),
        })),
      setProfileAnswers,
    }),
    [
      stepId,
      stepIndex,
      phase,
      answers,
      profileAnswers,
      userId,
      storageWarning,
      next,
      back,
      goToStep,
      setGovernmentId,
      setConfirmationVideo,
      addPhotos,
      removePhoto,
      setProfileAnswers,
      t,
    ]
  );

  if (!hydrated) {
    return (
      <div className="shell">
        <div className="shell__phone">
          <main className="shell__body">
            <p className="shell__hint" role="status">
              Loading your place in the questions…
            </p>
          </main>
        </div>
      </div>
    );
  }

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}

export function useFlow() {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error("useFlow must be used within FlowProvider");
  return ctx;
}
